import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ControlledCheckbox, ControlNumberField } from 'components/Form';
import { ControlledSwitch } from 'components/Form/ControlledSwitch';
import { TooltipWrapper } from 'components/TooltipWrapper';
import { BadgeWithTooltip } from 'components/WithTooltip/BadgeWithTooltip';
import type {
    WorkOrderChangeCostLimitDistributionData,
    WorkOrderChangeOtherCostObjectItem,
    WorkOrderChangeOtherPriceCostData,
    WorkOrderChangeSeasonData,
    WorkOrderCostLimitDistributionData,
    WorkOrderOtherCostObjectItem,
    WorkOrderOtherPriceCostData,
    WorkOrderSeasonData,
} from 'gql/graphql';
import { ChoiceGroup, Grid, GridItem, IconAlert, IconQuestion, IconWarning, Informer, TextField } from 'uikit';
import { formatNumberToRu } from 'utils';
import { replacerCost } from 'utils/replacerCost';
import { sumFloatNumbers } from 'utils/sumFloatNumbers';

import cn from 'classnames';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useFieldArray, useWatch } from 'react-hook-form';

import { useUerCalcWorkOrderDistribution } from './hooks/useUerCalcWorkOrderDistribution';

import Styles from './uerDistributionOtherAllocation.module.css';

type TUerAllocationProps = {
    control: Control;
    isWorkOrderChange: boolean;
    uerWorkOrderOtherDistributionData?:
        | {
              objects: (WorkOrderOtherCostObjectItem | WorkOrderChangeOtherCostObjectItem)[] | undefined;
              objectDistributionData: (WorkOrderOtherPriceCostData | WorkOrderChangeOtherPriceCostData)[] | undefined;
              objectLimitsData:
                  | WorkOrderCostLimitDistributionData
                  | WorkOrderChangeCostLimitDistributionData
                  | undefined;
          }[]
        | null
        | undefined;
    costRounding: number;
    workOrderOtherPriceId: number | undefined;
    contractOtherCostId: number | undefined;
    watch: UseFormWatch<any> | undefined;
    setValue: UseFormSetValue<any> | undefined;
    workOrderChangeCompositeIds: string | undefined;
    isCalculating: boolean | undefined;
};

export const UerDistributionOtherAllocation = (props: TUerAllocationProps) => {
    const {
        control,
        isWorkOrderChange,
        uerWorkOrderOtherDistributionData,
        costRounding,
        workOrderOtherPriceId,
        contractOtherCostId,
        watch,
        setValue,
        workOrderChangeCompositeIds,
        isCalculating,
    } = props;

    const [currentSeason, setCurrentSeason] = useState<(typeof seasons)[0]>();
    const handleChangeCurrentSeason = useCallback(({ value }) => {
        setCurrentSeason(value);
    }, []);

    const seasons = useMemo(
        () => uerWorkOrderOtherDistributionData?.map((data) => data?.objectLimitsData?.season) ?? [],
        [uerWorkOrderOtherDistributionData],
    );

    useEffect(() => {
        if (seasons) {
            setCurrentSeason(seasons[0]);
        }
    }, [seasons]);

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'objectDistribution',
    });

    useEffect(() => {
        if (uerWorkOrderOtherDistributionData) {
            uerWorkOrderOtherDistributionData?.forEach((root) => {
                const isSystemCalculation = root?.objectLimitsData?.isSystemCalculation;
                const seasonId = root?.objectLimitsData?.season?.id;
                const seasonName = root?.objectLimitsData?.season?.year;

                const objectsWithDistributionData = root?.objectDistributionData
                    ?.filter((od) => od.seasonId === seasonId)
                    ?.map((o) => {
                        const currentObjectData = root?.objects?.find((obj) => {
                            if (isWorkOrderChange) {
                                const typedObj = obj as WorkOrderChangeOtherCostObjectItem;
                                const typedDistribution = o as WorkOrderChangeOtherPriceCostData;

                                return (
                                    typedObj.rootObjectId?.workOrderChangeObjectId ===
                                        typedDistribution.workOrderChangeObjectId &&
                                    typedObj.rootObjectId?.workOrderObjectId === typedDistribution.workOrderObjectId
                                );
                            }

                            return (
                                (obj as WorkOrderOtherCostObjectItem).workOrderRootObjectId ===
                                (o as WorkOrderOtherPriceCostData).objectId
                            );
                        });

                        return {
                            ...o,
                            objectName: currentObjectData?.name,
                            canRemove: currentObjectData?.canRemoveOther,
                        };
                    });

                append({
                    isSystemCalculation,
                    seasonName,
                    seasonId,
                    seasonObjects: objectsWithDistributionData,
                });
            });
        }
    }, [append, isCalculating, isWorkOrderChange, uerWorkOrderOtherDistributionData]);

    const manualDistributionData: {
        isSystemCalculation: boolean;
        seasonName: string;
        seasonId: number;
        seasonObjects:
            | ((WorkOrderOtherPriceCostData | WorkOrderChangeOtherPriceCostData) & {
                  objectName: string;
              })[]
            | undefined;
    }[] = useWatch({
        control,
        name: 'objectDistribution',
    });

    const includedCostsData: Record<number, string | null | undefined> = useWatch({
        control,
        name: 'includedCost',
    });

    const isIncludedData: Record<number, { isIncluded: boolean }> = useWatch({
        control,
        name: 'isIncluded',
    });

    const checkIsSeasonDisabledByLimitsTab = useCallback(
        (seasonId: number) => {
            if (isCalculating) {
                return replacerCost(includedCostsData?.[seasonId]) === 0 || !includedCostsData[seasonId];
            }

            return !isIncludedData?.[seasonId]?.isIncluded;
        },
        [includedCostsData, isCalculating, isIncludedData],
    );

    const getCurrentSeasonIncludedValue = useCallback(
        (seasonId: number) => replacerCost(includedCostsData?.[seasonId] ?? 0),
        [includedCostsData],
    );

    const getCurrentSeasonFieldsData = useCallback(
        (seasonId: number) => manualDistributionData?.find((d) => d.seasonId === seasonId),
        [manualDistributionData],
    );

    const getCurrentSeasonDistributionSum = useCallback(
        (seasonId: number) =>
            getCurrentSeasonFieldsData(seasonId)?.seasonObjects?.reduce(
                (accSum: number, obj) => sumFloatNumbers(accSum, obj?.isIncluded ? replacerCost(obj?.cost ?? 0) : 0),
                0,
            ),
        [getCurrentSeasonFieldsData],
    );

    const getCurrentSeasonIsSystemCalc = useCallback(
        (seasonId: number) => getCurrentSeasonFieldsData(seasonId)?.isSystemCalculation,
        [getCurrentSeasonFieldsData],
    );

    const checkAreSumsNotEqual = useCallback(
        (seasonId: number) => getCurrentSeasonDistributionSum(seasonId) !== getCurrentSeasonIncludedValue(seasonId),
        [getCurrentSeasonDistributionSum, getCurrentSeasonIncludedValue],
    );

    const checkIsNoOneObjectChecked = useCallback(
        (seasonId: number) =>
            getCurrentSeasonFieldsData(seasonId)?.seasonObjects?.every(({ isIncluded }) => !isIncluded),
        [getCurrentSeasonFieldsData],
    );

    // clean up array fields
    useEffect(
        () => () => {
            remove();
        },
        [remove],
    );

    const onCalcSuccess = useCallback(
        (items: (WorkOrderOtherPriceCostData | WorkOrderChangeOtherPriceCostData | null)[] | null | undefined) => {
            fields?.forEach((formField: any, idx) => {
                const updateItemSeasonData = items?.filter((u) => u?.seasonId === formField?.seasonId);

                if (updateItemSeasonData?.length) {
                    let systemCalcIncludedCost = 0;

                    const someWithErrors = updateItemSeasonData?.some((data) => data?.hasErrors);

                    formField?.seasonObjects?.forEach(
                        (
                            f: { objectId?: number; workOrderChangeObjectId?: number; workOrderObjectId?: number },
                            fieldIdx: number,
                        ) => {
                            const key = `objectDistribution.${idx}.seasonObjects.${fieldIdx}.cost`;
                            const hasErrorsKey = `objectDistribution.${idx}.seasonObjects.${fieldIdx}.hasErrors`;
                            const errorsKey = `objectDistribution.${idx}.seasonObjects.${fieldIdx}.errors`;

                            const {
                                cost: currentValue,
                                errors,
                                hasErrors,
                            } = updateItemSeasonData?.find((data) => {
                                if (isWorkOrderChange) {
                                    const typed = data as WorkOrderChangeOtherPriceCostData;

                                    return (
                                        f.workOrderChangeObjectId === typed?.workOrderChangeObjectId &&
                                        f.workOrderObjectId === typed?.workOrderObjectId
                                    );
                                }

                                return f.objectId === (data as WorkOrderOtherPriceCostData)?.objectId;
                            }) ?? {};

                            if (currentValue !== undefined && currentValue !== null) {
                                systemCalcIncludedCost = sumFloatNumbers(systemCalcIncludedCost, currentValue);
                                setValue?.(key, currentValue);
                            } else {
                                setValue?.(key, '');
                            }
                            setValue?.(errorsKey, errors);
                            setValue?.(hasErrorsKey, hasErrors);
                        },
                    );

                    if (!someWithErrors) {
                        if (isCalculating) {
                            setValue?.(`isIncluded.${formField.seasonId}`, { isIncluded: true });
                        } else {
                            setValue?.(`includedCost.${formField.seasonId}`, systemCalcIncludedCost);
                        }
                    }
                }
            });
        },
        [fields, isCalculating, isWorkOrderChange, setValue],
    );

    const onCalcError = useCallback(() => {
        const index = manualDistributionData.findIndex((m: { seasonId: number }) => m.seasonId === currentSeason?.id);

        setValue?.(`objectDistribution.${index}.isSystemCalculation`, false);
    }, [currentSeason?.id, manualDistributionData, setValue]);

    const { calcWorkOrderDistributionRequest, calcInProgress } = useUerCalcWorkOrderDistribution({
        workOrderOtherPriceId,
        onCalcSuccess,
        onCalcError,
        isWorkOrderChange,
        workOrderChangeCompositeIds,
    });

    useEffect(() => {
        const formSubscription = watch?.((value, { name, type }) => {
            if (type === 'change' || type === undefined) {
                const [root, seasonIndex, fieldName, , objectFieldName] = (name ?? '').split('.');
                const numberSeasonIndex = Number(seasonIndex);

                /* для прочей если изначально сезон на вкладке лимитов выключен,
                 * isIncluded === false и includedCosts отсутствует
                 * то, после включения сезона, форсированно для текущего сезона
                 * устанавливаем чекбоксы объектов и isSystemCalculation в true.
                 *
                 * для прочей расчет, если isSystemCalculation === true и изменяется лимит сезона,
                 * форсированно пересчитываем распределение
                 *
                 * event name: isIncluded.{seasonId}
                 * */
                if (root?.includes('isIncluded') || root?.includes('includedCost')) {
                    const isForceSystemCalculation = isCalculating
                        ? !value.isIncluded?.[numberSeasonIndex]?.isIncluded && value.includedCost?.[numberSeasonIndex]
                        : value.isIncluded?.[numberSeasonIndex]?.isIncluded &&
                          value.includedCost?.[numberSeasonIndex] === null;

                    const seasonObjIdx = value?.objectDistribution?.findIndex(
                        (o: { seasonId: number }) => o.seasonId === numberSeasonIndex,
                    );

                    if (isForceSystemCalculation) {
                        setValue?.(`objectDistribution.${seasonObjIdx}.isSystemCalculation`, true);
                    } else {
                        const isSystemCalculation = value?.objectDistribution?.[seasonObjIdx]?.isSystemCalculation;

                        if (type === 'change' && isSystemCalculation && isCalculating) {
                            setValue?.(`objectDistribution.${seasonObjIdx}.isSystemCalculation`, true);
                        }
                    }

                    return;
                }

                /*
                 * event name: change objectDistribution.{objectDistributionIndex}.isSystemCalculation
                 * event name: objectDistribution.{objectDistributionIndex}.seasonObjects.{seasonObjectsIndex}.isIncluded
                 */
                if (fieldName?.includes('isSystemCalculation') || objectFieldName?.includes('isIncluded')) {
                    const calcSeasonId = value[root][numberSeasonIndex].seasonId;
                    const isSystem = value[root][numberSeasonIndex].isSystemCalculation;
                    const currentSeasonIncludedCost = replacerCost(value?.includedCost?.[calcSeasonId]) ?? null;

                    if (isSystem) {
                        const calcObjects = value?.objectDistribution?.[seasonIndex]?.seasonObjects
                            ?.filter(
                                (o: { seasonId: number; isIncluded: boolean }) =>
                                    o.seasonId === calcSeasonId && o.isIncluded,
                            )
                            .map(
                                (o: {
                                    objectId?: number;
                                    workOrderObjectId?: number;
                                    workOrderChangeObjectId?: number;
                                }) => {
                                    if (isWorkOrderChange) {
                                        return {
                                            workOrderChangeObjectId: o.workOrderChangeObjectId,
                                            workOrderObjectId: o.workOrderObjectId,
                                        };
                                    }

                                    return o.objectId;
                                },
                            );

                        if (!calcObjects?.length) {
                            setValue?.(`objectDistribution.${seasonIndex}.isSystemCalculation`, false);

                            return;
                        }

                        if (isWorkOrderChange) {
                            calcWorkOrderDistributionRequest({
                                contractOtherCostId,
                                ...(isCalculating && { includedCost: currentSeasonIncludedCost }),
                                seasonId: calcSeasonId,
                                workOrderChangeObjectItemIds: calcObjects,
                            });
                        } else {
                            calcWorkOrderDistributionRequest({
                                contractOtherCostId,
                                ...(isCalculating && { includedCost: currentSeasonIncludedCost }),
                                seasonId: calcSeasonId,
                                workOrderObjectIds: calcObjects,
                            });
                        }
                    }
                }
            }
        });

        return () => {
            formSubscription?.unsubscribe();
        };
    }, [
        calcWorkOrderDistributionRequest,
        contractOtherCostId,
        currentSeason?.id,
        getCurrentSeasonIncludedValue,
        isCalculating,
        isWorkOrderChange,
        setValue,
        watch,
    ]);

    return (
        <>
            {Array.isArray(seasons) && (
                <div className={Styles.otherDistributionSeasonContainer}>
                    <ChoiceGroup
                        value={currentSeason}
                        onChange={handleChangeCurrentSeason}
                        items={seasons as any}
                        getItemLabel={(item: WorkOrderSeasonData | WorkOrderChangeSeasonData) => `ceзон ${item.year}`}
                        getItemIcon={(item) =>
                            checkAreSumsNotEqual(item?.id) && !checkIsSeasonDisabledByLimitsTab(item?.id)
                                ? IconWarning
                                : undefined
                        }
                        view="ghost"
                        multiple={false}
                        name="otherDistributionSeason"
                        size="s"
                        width="default"
                        className={Styles.otherDistributionSeason}
                    />
                </div>
            )}

            {checkAreSumsNotEqual(currentSeason?.id) && !checkIsSeasonDisabledByLimitsTab(currentSeason?.id) && (
                <Informer
                    status="alert"
                    label={`Сумма лимитов должна быть равна сумме включенной в ${
                        isWorkOrderChange ? 'Изм к НЗ' : 'НЗ'
                    }`}
                    icon={IconAlert}
                    className={Styles.allocationInformer}
                />
            )}

            {fields?.map((field: any, index) => {
                if (field.seasonId === currentSeason?.id) {
                    const seasonDisabledByLimits = checkIsSeasonDisabledByLimitsTab(field.seasonId);
                    const areSumsNotEqual = checkAreSumsNotEqual(field.seasonId);
                    const currentSeasonDistributionSum = getCurrentSeasonDistributionSum(field.seasonId);
                    const currentSeasonIncludedValue = getCurrentSeasonIncludedValue(field.seasonId);
                    const currentSeasonIsSystemCalc = getCurrentSeasonIsSystemCalc(field.seasonId);
                    const systemCalcDisabledByObjects = checkIsNoOneObjectChecked(field.seasonId);

                    return (
                        <Fragment key={field.seasonId}>
                            <div className={Styles.distributionOtherAllocationPanel}>
                                <ComponentWithTooltip
                                    tooltipText={
                                        systemCalcDisabledByObjects && !seasonDisabledByLimits && !calcInProgress
                                            ? 'Для автоматического расчета должен быть выбран хотя бы один объект'
                                            : ''
                                    }
                                >
                                    <ControlledSwitch
                                        size="s"
                                        label="Распределить лимиты автоматически"
                                        control={control}
                                        name={`objectDistribution.${index}.isSystemCalculation`}
                                        disabled={
                                            calcInProgress || seasonDisabledByLimits || systemCalcDisabledByObjects
                                        }
                                    />
                                </ComponentWithTooltip>

                                <div className={Styles.distributionOtherAllocationSums}>
                                    <span
                                        className={cn(Styles.distributionOtherAllocationLimitsSum, {
                                            [Styles.distributionOtherAllocationLimitsSumAlert]:
                                                areSumsNotEqual && !seasonDisabledByLimits,
                                        })}
                                    >
                                        {seasonDisabledByLimits
                                            ? 0
                                            : formatNumberToRu(currentSeasonDistributionSum, costRounding)}{' '}
                                        ₽
                                    </span>
                                    <span className={Styles.slash}>/</span>
                                    {seasonDisabledByLimits
                                        ? 0
                                        : formatNumberToRu(currentSeasonIncludedValue, costRounding)}{' '}
                                    ₽
                                </div>
                                <TooltipWrapper
                                    message={isCalculating ? 'Сумма лимитов / Включено в НЗ' : 'Сумма лимитов'}
                                    className={Styles.distributionOtherAllocationQuestion}
                                >
                                    <IconQuestion size="s" />
                                </TooltipWrapper>
                            </div>
                            {field?.seasonObjects?.map(
                                (
                                    objectField: {
                                        objectId?: number;
                                        workOrderChangeObjectId?: number;
                                        workOrderObjectId?: number;
                                        objectName: string;
                                        hasErrors: boolean | null;
                                        errors: string[];
                                        isIncluded: boolean;
                                        canRemove: boolean;
                                    },
                                    objectFieldIdx: number,
                                ) => {
                                    const currentIsNotChecked =
                                        !manualDistributionData?.[index]?.seasonObjects?.[objectFieldIdx]?.isIncluded;
                                    const isSystemCalc = manualDistributionData?.[index]?.isSystemCalculation;
                                    const isErrorValue = Boolean(objectField?.hasErrors && isSystemCalc);
                                    const isDisabledByObjOptions = objectField?.isIncluded && !objectField?.canRemove;

                                    const key = isWorkOrderChange
                                        ? `${index}_${objectField.workOrderChangeObjectId}_${objectField.workOrderObjectId}`
                                        : `${index}_${objectField.objectId}`;

                                    return (
                                        <Grid
                                            rowGap="xs"
                                            cols="3"
                                            gap="xs"
                                            className={Styles.distributionOtherAllocationGrid}
                                            key={key}
                                        >
                                            <GridItem key={`${field.id}-1`} col="2">
                                                <ControlledCheckbox
                                                    key={`${field.id}-1-check`}
                                                    name={`objectDistribution.${index}.seasonObjects.${objectFieldIdx}.isIncluded`}
                                                    label={objectField.objectName}
                                                    size="m"
                                                    control={control}
                                                    disabled={
                                                        calcInProgress ||
                                                        seasonDisabledByLimits ||
                                                        isDisabledByObjOptions
                                                    }
                                                />
                                            </GridItem>
                                            <GridItem key={`${field.id}-2`}>
                                                {isErrorValue && (
                                                    <BadgeWithTooltip
                                                        messages={objectField?.errors}
                                                        size="l"
                                                        badgeSize="s"
                                                        label="Ошибка"
                                                        status="error"
                                                        iconLeft={IconWarning}
                                                        className={Styles.badgeStyle}
                                                    />
                                                )}
                                                {!isErrorValue &&
                                                    (currentIsNotChecked || seasonDisabledByLimits ? (
                                                        <TextField disabled size="s" />
                                                    ) : (
                                                        <ControlNumberField
                                                            key={`${field.id}-2-input`}
                                                            name={`objectDistribution.${index}.seasonObjects.${objectFieldIdx}.cost`}
                                                            control={control}
                                                            size="s"
                                                            decimalScale={costRounding}
                                                            disabled={
                                                                currentIsNotChecked ||
                                                                calcInProgress ||
                                                                currentSeasonIsSystemCalc ||
                                                                seasonDisabledByLimits
                                                            }
                                                        />
                                                    ))}
                                            </GridItem>
                                        </Grid>
                                    );
                                },
                            )}
                        </Fragment>
                    );
                }

                return undefined;
            })}
        </>
    );
};

UerDistributionOtherAllocation.displayName = 'UerDistributionOtherAllocation';
